import { ReportViewConfig } from './report-view.model';

/**
 * The report destinations, one per route.
 *
 * All of these embed the SAME Power BI report — the tenant's `ReportId` from
 * tblConfiguration. What differs is which of its pages each one shows, which is
 * data (`Type='PbiPage'` rows in tblPageFields), not code. Adding a destination
 * means a config here, a route, and a tblPage row on the tenants that should
 * have it; re-cutting pages between existing destinations means neither.
 *
 * `pageKey` must match `tblPage.PageName` on the tenant, and `cacheKey` must be
 * unique per destination — it namespaces the global-search page cache
 * (`PBIPages_<cacheKey>_<orgId>`).
 */

/**
 * The original Spend dashboard.
 *
 * `whenUnassigned: 'all'` is load-bearing: no tenant except Yunex has any
 * assignment rows, so every one of them takes the fallback and sees the whole
 * report exactly as before. Assignment only narrows this page for a tenant that
 * has explicitly opted in by creating rows.
 *
 * `cacheKey` stays `spend` so global search keeps finding dashboard pages under
 * the key it already writes and reads.
 */
export const SPEND_DASHBOARD_DESTINATION: ReportViewConfig = {
  pageKey: 'DashboardPage',
  cacheKey: 'spend',
  title: 'Dashboard - Spendkey',
  whenUnassigned: 'all',
};

export const ESG_DESTINATION: ReportViewConfig = {
  pageKey: 'EsgAnalytics',
  cacheKey: 'esg',
  title: 'ESG - Spendkey',
  whenUnassigned: 'empty',
};

export const CO2_DESTINATION: ReportViewConfig = {
  pageKey: 'Co2Analytics',
  cacheKey: 'co2',
  title: 'CO2 - Spendkey',
  whenUnassigned: 'empty',
};

export const PROCUREMENT_PERFORMANCE_DESTINATION: ReportViewConfig = {
  pageKey: 'ProcurementPerformanceCockpit',
  cacheKey: 'procurement-performance',
  title: 'Procurement Performance Cockpit - Spendkey',
  whenUnassigned: 'empty',
};

export const PROCUREMENT_RISK_DESTINATION: ReportViewConfig = {
  pageKey: 'ProcurementRiskCockpit',
  cacheKey: 'procurement-risk',
  title: 'Procurement Risk Cockpit - Spendkey',
  whenUnassigned: 'empty',
};

export const SPEND_INTELLIGENCE_DESTINATION: ReportViewConfig = {
  pageKey: 'SpendIntelligence',
  cacheKey: 'spend-intelligence',
  title: 'Spend Intelligence - Spendkey',
  whenUnassigned: 'empty',
};

/**
 * `tblPage.Link` of every menu page that renders the Power BI report.
 *
 * Menu Settings uses this to decide which rows get a "report pages" panel —
 * assigning report pages to, say, Users would be meaningless. `dashboard` is in
 * the set because it is a report destination too, and the one that can be
 * narrowed from "everything" to a chosen subset.
 *
 * Kept next to the configs so a new destination cannot be added without the
 * admin screen learning about it.
 */
export const REPORT_DESTINATION_LINKS: ReadonlySet<string> = new Set([
  'dashboard',
  'esg-analytics',
  'co2-analytics',
  'procurement-performance-cockpit',
  'procurement-risk-cockpit',
  'spend-intelligence',
]);
